const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const staffingCache = new Map(); // key: outcomeId

export const clearStaffingCache = (outcomeIds = null) => {
  if (outcomeIds === null) { staffingCache.clear(); }
  else { outcomeIds.forEach(id => staffingCache.delete(id)); }
};

const spendCache = new Map(); // key: `${outcomeId}_${year}`

export const clearSpendCache = (outcomeIds = null, year = null) => {
  if (outcomeIds === null) { spendCache.clear(); return; }
  outcomeIds.forEach(id => {
    if (year !== null) { spendCache.delete(`${id}_${year}`); }
    else { for (const k of spendCache.keys()) { if (k.startsWith(`${id}_`)) spendCache.delete(k); } }
  });
};

// Generic API functions
export const get = async (url) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API GET error:', error);
    throw error;
  }
};

export const post = async (url, data) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API POST error:', error);
    throw error;
  }
};

export const put = async (url, data) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API PUT error:', error);
    throw error;
  }
};

export const del = async (url) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API DELETE error:', error);
    throw error;
  }
};

// Outcome-related API functions
export const getOutcomes = async (department = null) => {
  try {
    const url = department
      ? `${API_BASE_URL}/api/outcomes?department=${encodeURIComponent(department)}`
      : `${API_BASE_URL}/api/outcomes`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // Return the outcomes array directly
    return Array.isArray(data) ? data : data.outcomes || [];
  } catch (error) {
    console.error('Error fetching outcomes:', error);
    throw error;
  }
};

export const getOutcomesLightweight = async (status = null) => {
  try {
    const startTime = performance.now();
    const url = status ? `/api/outcomes?status=${status}` : '/api/outcomes';
    const response = await fetch(`${API_BASE_URL}${url}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    const endTime = performance.now();
    const clientTime = endTime - startTime;
    
    // Log performance metrics
    console.log(`🚀 Outcomes API Performance:`, {
      endpoint: url,
      client_time: `${clientTime.toFixed(2)}ms`,
      total_count: data.outcomes ? data.outcomes.length : 0,
      data_size: `${JSON.stringify(data).length} chars`
    });
    
    // Handle the response format
    return data.outcomes || [];
  } catch (error) {
    console.error('Error fetching outcomes:', error);
    throw error;
  }
};

// Function to get outcomes with specific statuses
export const getOutcomesLightweightByStatus = async (statuses) => {
  try {
    if (Array.isArray(statuses) && statuses.length === 1) {
      // Single status - use status parameter
      return await getOutcomesLightweight(statuses[0]);
    } else if (Array.isArray(statuses) && statuses.length > 1) {
      // Multiple statuses - fetch all and filter client-side
      const allOutcomes = await getOutcomesLightweight();
      return allOutcomes.filter(outcome => statuses.includes(outcome.status));
    } else {
      // No status filter
      return await getOutcomesLightweight();
    }
  } catch (error) {
    console.error('Error fetching outcomes by status:', error);
    throw error;
  }
};

export const getOutcome = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error(`Error fetching outcome ${id}:`, error);
    throw error;
  }
};

export const createOutcome = async (outcomeData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(outcomeData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error creating outcome:', error);
    throw error;
  }
};

export const updateOutcome = async (id, outcomeData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(outcomeData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error updating outcome ${id}:`, error);
    throw error;
  }
};

export const deleteOutcome = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${id}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const clearApprovedBudget = async (outcomeId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/clear-approved-budget`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error clearing approved budget for outcome ${outcomeId}:`, error);
    throw error;
  }
};

// Cost Estimation API functions
export const getCostEstimation = async (outcomeId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/cost-estimation`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error(`Error fetching cost estimation for outcome ${outcomeId}:`, error);
    throw error;
  }
};

export const saveCostEstimation = async (outcomeId, costData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/cost-estimation`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(costData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error saving cost estimation for outcome ${outcomeId}:`, error);
    throw error;
  }
};

// Financial Model API functions
export const getFinancialModel = async (outcomeId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/financial-model`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error(`Error fetching financial model for outcome ${outcomeId}:`, error);
    throw error;
  }
};

export const updateFinancialModel = async (outcomeId, modelData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/financial-model`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(modelData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error updating financial model for outcome ${outcomeId}:`, error);
    throw error;
  }
};

// Staffing API functions
export const getStaffingData = async (outcomeId) => {
  if (staffingCache.has(outcomeId)) return staffingCache.get(outcomeId);
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/staffing`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  const result = data.data || data;
  staffingCache.set(outcomeId, result);
  return result;
};

// Roster-specific staffing data that excludes cost estimation roles
export const getRosterStaffingData = async (outcomeId) => {
  const response = await fetch(`${API_BASE_URL}/api/roster/outcomes/${outcomeId}/staffing`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return data.data || data;
};

export const saveStaffingData = async (outcomeId, staffingData) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/staffing`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(staffingData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  clearStaffingCache([outcomeId]);
  const data = await response.json();
  return data.data || data;
};

// IT Roster API functions
export const getITRoster = async (department = null) => {
  const url = department
    ? `${API_BASE_URL}/api/it-roster?department=${encodeURIComponent(department)}`
    : `${API_BASE_URL}/api/it-roster`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const addITRosterMember = async (memberData) => {
  const response = await fetch(`${API_BASE_URL}/api/it-roster`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(memberData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const updateITRosterMember = async (memberId, memberData) => {
  const response = await fetch(`${API_BASE_URL}/api/it-roster/${memberId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(memberData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const deleteITRosterMember = async (memberId) => {
  const response = await fetch(`${API_BASE_URL}/api/it-roster/${memberId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Rates API functions
export const getRates = async () => {
  const response = await fetch(`${API_BASE_URL}/api/rates`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createRate = async (rateData) => {
  const response = await fetch(`${API_BASE_URL}/api/rates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(rateData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const updateRate = async (rateIndex, rateData) => {
  const response = await fetch(`${API_BASE_URL}/api/rates/${rateIndex}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(rateData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const deleteRate = async (rateIndex) => {
  const response = await fetch(`${API_BASE_URL}/api/rates/${rateIndex}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// COLA-enhanced rates API function
export const getRateWithColaAdjustment = async (company, location, role, targetYear) => {
  const response = await fetch(`${API_BASE_URL}/api/rates/with-cola`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      company,
      location,
      role,
      target_year: targetYear
    }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Unassigned Roles API functions
export const getUnassignedRoles = async (year) => {
  const response = await fetch(`${API_BASE_URL}/api/unassigned-roles/${year}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Roster-specific unassigned roles that exclude cost estimation roles
export const getRosterUnassignedRoles = async (year) => {
  const response = await fetch(`${API_BASE_URL}/api/roster/unassigned-roles/${year}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const assignRole = async (roleId, memberId) => {
  const response = await fetch(`${API_BASE_URL}/api/assign-role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      role_id: roleId,
      member_id: memberId,
    }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Applications API functions
export const getApplications = async () => {
  const response = await fetch(`${API_BASE_URL}/api/applications`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  // Backend returns array directly, but components expect {applications: [...]}
  if (Array.isArray(data)) {
    return { applications: data };
  }
  return data;
};

export const createApplication = async (applicationData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/applications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(applicationData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error creating application:', error);
    throw error;
  }
};

export const updateApplication = async (applicationId, applicationData) => {
  const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(applicationData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const deleteApplication = async (applicationId) => {
  const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Departments API functions
export const getDepartments = async () => {
  const response = await fetch(`${API_BASE_URL}/api/departments`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (Array.isArray(data)) {
    return { departments: data };
  }
  return data;
};

export const createDepartment = async (departmentData) => {
  const response = await fetch(`${API_BASE_URL}/api/departments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(departmentData),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateDepartment = async (departmentId, departmentData) => {
  const response = await fetch(`${API_BASE_URL}/api/departments/${departmentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(departmentData),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const deleteDepartment = async (departmentId) => {
  const response = await fetch(`${API_BASE_URL}/api/departments/${departmentId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createCostCenter = async (departmentId, data) => {
  const response = await fetch(`${API_BASE_URL}/api/departments/${departmentId}/cost_centers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const updateCostCenter = async (departmentId, costCenterId, data) => {
  const response = await fetch(`${API_BASE_URL}/api/departments/${departmentId}/cost_centers/${costCenterId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const deleteCostCenter = async (departmentId, costCenterId) => {
  const response = await fetch(`${API_BASE_URL}/api/departments/${departmentId}/cost_centers/${costCenterId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

// Owners API functions
export const getOwners = async () => {
  const response = await fetch(`${API_BASE_URL}/api/owners`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createOwner = async (ownerData) => {
  try {
    console.log('API: Creating owner with data:', ownerData);
    
    const response = await fetch(`${API_BASE_URL}/api/owners`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ownerData),
    });
    
    console.log('API: Create owner response status:', response.status);
    
    if (!response.ok) {
      let errorDetails;
      try {
        const errorText = await response.text();
        console.log('API: Error response text:', errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          console.log('API: Parsed error JSON:', errorJson);
          errorDetails = errorJson.detail || errorJson.message || errorJson.error || errorText;
        } catch (parseError) {
          console.log('API: Could not parse error as JSON:', parseError);
          errorDetails = errorText;
        }
      } catch (textError) {
        console.log('API: Could not read error text:', textError);
        errorDetails = 'Unknown server error';
      }
      
      const fullErrorMessage = `HTTP error! status: ${response.status}, details: ${errorDetails}`;
      console.error('API: Full error message:', fullErrorMessage);
      throw new Error(fullErrorMessage);
    }
    
    const result = await response.json();
    console.log('API: Create owner success result:', result);
    return result;
  } catch (error) {
    console.error('API: Error creating owner:', error);
    throw error;
  }
};

// Add alias for AdminPage.jsx compatibility
export const addOwner = createOwner;

// Add alias for ApplicationsTab.jsx compatibility
export const addApplication = createApplication;

// Tags API functions
export const getTags = async () => {
  const response = await fetch(`${API_BASE_URL}/api/tags`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  // Backend returns array directly, but components expect {tags: [...]}
  if (Array.isArray(data)) {
    return { tags: data };
  }
  return data;
};

export const createTag = async (tagData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/tags`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tagData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error creating tag:', error);
    throw error;
  }
};

export const updateTag = async (tagId, tagData) => {
  const response = await fetch(`${API_BASE_URL}/api/tags/${tagId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(tagData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const deleteTag = async (tagId) => {
  const response = await fetch(`${API_BASE_URL}/api/tags/${tagId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Tag Association API functions
export const assignTagToMember = async (memberId, tagId) => {
  const response = await fetch(`${API_BASE_URL}/api/it-roster/${memberId}/tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tag_id: tagId }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const removeTagFromMember = async (memberId, tagId) => {
  const response = await fetch(`${API_BASE_URL}/api/it-roster/${memberId}/tags/${tagId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const assignTagToOutcome = async (outcomeId, tagId) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tag_id: tagId }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const removeTagFromOutcome = async (outcomeId, tagId) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/tags/${tagId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Get tags for a specific member
export const getMemberTags = async (memberId) => {
  const response = await fetch(`${API_BASE_URL}/api/it-roster/${memberId}/tags`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Get tags for a specific outcome
export const getOutcomeTags = async (outcomeId) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/tags`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateOwner = async (ownerId, ownerData) => {
  const response = await fetch(`${API_BASE_URL}/api/owners/${ownerId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ownerData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const deleteOwner = async (ownerId) => {
  const response = await fetch(`${API_BASE_URL}/api/owners/${ownerId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const syncFunctionalOwners = async () => {
  const response = await fetch(`${API_BASE_URL}/api/owners/sync-functional`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Plans API functions
export const getPlans = async () => {
  const response = await fetch(`${API_BASE_URL}/api/plans`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const getPlan = async (planId) => {
  const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createPlan = async (planData) => {
  const response = await fetch(`${API_BASE_URL}/api/plans`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(planData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const updatePlan = async (planId, planData) => {
  const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(planData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const deletePlan = async (planId) => {
  const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const copyPlan = async (planId) => {
  const response = await fetch(`${API_BASE_URL}/api/plans/${planId}/copy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// COLA Settings API functions
export const getColaSettings = async () => {
  const response = await fetch(`${API_BASE_URL}/api/cola-settings`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Add aliases for different naming conventions used in components
export const getCOLASettings = getColaSettings;

export const updateColaSettings = async (settings) => {
  const response = await fetch(`${API_BASE_URL}/api/cola-settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Add alias for different naming convention
export const updateCOLASettings = updateColaSettings;

// Spend Data API functions
export const getSpendData = async (outcomeId, year = null, retryCount = 0) => {
  const cacheKey = `${outcomeId}_${year ?? 'null'}`;
  if (retryCount === 0 && spendCache.has(cacheKey)) return spendCache.get(cacheKey);

  const url = year
    ? `${API_BASE_URL}/api/outcomes/${outcomeId}/spend?year=${year}`
    : `${API_BASE_URL}/api/outcomes/${outcomeId}/spend`;
  
  if (process.env.NODE_ENV === 'development' && (outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903' || retryCount > 0)) {
    console.log(`🌐 API: Fetching spend data for ${outcomeId}, year=${year}, retry=${retryCount}`);
    console.log(`🌐 API: URL = ${url}`);
  }
    
  const response = await fetch(url);
  
  // Handle 404 gracefully - it's expected when no spend data exists
  if (response.status === 404) {
    // If this is a retry after upload, wait a bit longer and try once more
    if (retryCount === 0 && typeof window !== 'undefined' && window.lastSpendUploadTime) {
      const timeSinceUpload = Date.now() - window.lastSpendUploadTime;
      if (timeSinceUpload < 10000) { // Within 10 seconds of upload
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔄 Retrying spend data fetch for ${outcomeId} after recent upload (${timeSinceUpload}ms ago)`);
        }
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        return getSpendData(outcomeId, year, retryCount + 1);
      }
    }
    return {}; // Return empty object instead of throwing error
  }
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
    const data = await response.json();
  
  if (process.env.NODE_ENV === 'development' && outcomeId === '2e66cd5c-2469-4284-a881-7aa4c0990903') {
    console.log(`🔍 RAW API RESPONSE for ${outcomeId}:`);
    console.log('  📊 Response Status:', response.status, response.statusText);
    console.log('  📋 Response Headers:', Object.fromEntries(response.headers.entries()));
    console.log('  📦 Response Data Keys:', Object.keys(data));
    console.log('  📦 FULL RESPONSE DATA:', data);
    console.log('  🔍 Key Fields:', {
      hasUploadedData: data.hasUploadedData,
      totalActual: data.totalActual,
      monthlySpend: data.monthlySpend,
      companySpend: data.companySpend,
      year: data.year
    });
  }
  
  spendCache.set(cacheKey, data);
  return data;
};

export const getSpendDataBulk = async (outcomeIds, year) => {
  const uncachedIds = outcomeIds.filter(id => !spendCache.has(`${id}_${year}`));
  if (uncachedIds.length === 0) return;
  const url = `${API_BASE_URL}/api/outcomes/spend/bulk?year=${year}&ids=${encodeURIComponent(uncachedIds.join(','))}`;
  try {
    const response = await fetch(url);
    if (!response.ok) { console.warn('Bulk spend fetch failed, falling back to individual calls'); return; }
    const bulkData = await response.json();
    Object.entries(bulkData).forEach(([id, data]) => spendCache.set(`${id}_${year}`, data));
  } catch (e) {
    console.warn('Bulk spend fetch error:', e);
  }
};

export const getUploadedOutcomeIds = async (year) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/outcomes/spend/uploaded-outcome-ids?year=${year}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.outcome_ids || [];
  } catch (e) {
    console.warn('getUploadedOutcomeIds error:', e);
    return [];
  }
};

// Add aliases for different naming conventions used in components
export const getOutcomeSpend = getSpendData;

export const updateSpendData = async (outcomeId, spendData) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/spend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(spendData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Add alias for different naming convention
export const saveOutcomeSpend = updateSpendData;

// Batch refresh rates for outcomes
export const batchRefreshOutcomeRates = async (outcomeIds, targetYear = new Date().getFullYear()) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/batch-refresh-rates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      outcome_ids: outcomeIds,
      target_year: targetYear
    }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Refresh rates for a single outcome
export const refreshOutcomeRates = async (outcomeId, targetYear = new Date().getFullYear()) => {
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/refresh-rates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      target_year: targetYear
    }),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Persist role updates back to outcome staffing data
export const persistOutcomeRoleUpdates = async (outcomeId, roleUpdates) => {
  console.log(`🔄 API: Calling persistOutcomeRoleUpdates for outcome ${outcomeId} with ${roleUpdates.length} updates`);
  
  const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/persist-role-updates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(roleUpdates), // Send roleUpdates directly, not wrapped in object
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`❌ API: persistOutcomeRoleUpdates failed:`, {
      status: response.status,
      statusText: response.statusText,
      errorText,
      outcomeId,
      roleUpdatesCount: roleUpdates.length
    });
    throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
  }
  
  const result = await response.json();
  console.log(`✅ API: persistOutcomeRoleUpdates succeeded:`, result);
  return result;
};

// Test persistence endpoint
export const testPersistence = async (outcomeId) => {
  console.log(`🧪 API: Testing persistence for outcome ${outcomeId}`);
  
  const response = await fetch(`${API_BASE_URL}/api/test-persistence/${outcomeId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const result = await response.json();
  console.log(`🧪 API: Persistence test result:`, result);
  return result;
};

// Assign role to member
export const assignRoleToMember = async (assignmentData) => {
  const response = await fetch(`${API_BASE_URL}/api/assign-role-to-member`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(assignmentData),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Member Allocations API functions
export const getMemberAllocations = async () => {
  const response = await fetch(`${API_BASE_URL}/api/member-allocations`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Update member rates (fallback function for when backend endpoint doesn't exist)
export const updateMemberRates = async (memberRateUpdates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/member-rates`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates: memberRateUpdates }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.warn('updateMemberRates: Backend endpoint not available, using fallback behavior');
    // Return a mock success response for fallback behavior
    return {
      success: true,
      updated_count: memberRateUpdates.length,
      message: 'Member rates updated via fallback (local storage)',
      fallback: true
    };
  }
};

export const getOutcomeAllocations = async (year) => {
  const response = await fetch(`${API_BASE_URL}/api/outcome-allocations/${year}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Roster-specific API functions that exclude cost estimation data
export const getRosterOutcomeAllocations = async (year) => {
  console.log(`📊 API: Fetching roster-specific outcome allocations for year ${year}`);
  const response = await fetch(`${API_BASE_URL}/api/roster/outcome-allocations/${year}`);
  if (!response.ok) {
    console.warn(`📊 API: Roster allocations endpoint failed (${response.status}), will fallback to general endpoint`);
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  
  // Debug the actual response structure
  console.log(`🔍 API: Full response structure:`, {
    keys: Object.keys(data),
    outcome_allocations_length: data.outcome_allocations?.length || 0,
    direct_array_length: Array.isArray(data) ? data.length : 'not array',
    sample_allocation: data.outcome_allocations?.[0] || data[0] || 'none'
  });
  
  console.log(`✅ API: Successfully fetched ${data.outcome_allocations?.length || data.length || 0} roster-specific outcomes`);
  return data;
};

// Enhanced roster-specific unassigned roles (already exists above, but ensuring consistency)
// export const getRosterUnassignedRoles is already defined above

// Roster-specific staffing data (already exists above as getRosterStaffingData)
// This ensures we have all roster endpoints properly defined

// Health check
export const healthCheck = async () => {
  const response = await fetch(`${API_BASE_URL}/health`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// S3 connectivity check
export const checkS3Status = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/s3-diagnosis`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error checking S3 status:', error);
    throw error;
  }
};

// Debug spend data endpoint
export const debugSpendData = async (outcomeId, year) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/debug/spend/${outcomeId}/${year}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error debugging spend data:', error);
    throw error;
  }
};

// Clear uploaded data endpoint
// clearType: "all" | "actuals" | "forecast"
// department: optional string — limit clear to records tagged to that dept
export const clearUploadedData = async (password, clearType = 'all', department = null) => {
  try {
    const body = { password, clear_type: clearType };
    if (department) body.department = department;
    const response = await fetch(`${API_BASE_URL}/api/clear-uploaded-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to clear data' }));
      throw new Error(errorData.detail || 'Failed to clear data');
    }

    return response.json();
  } catch (error) {
    console.error('Error clearing data:', error);
    throw error;
  }
};

// Storage info check
export const getStorageInfo = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/storage-info`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error getting storage info:', error);
    throw error;
  }
};

// Member Propagation API functions
export const findOutcomesWithMember = async (memberId) => {
  try {
    console.log(`🔍 API: Finding outcomes with member ${memberId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/members/${memberId}/outcomes`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log(`✅ API: Found ${data.outcomes?.length || 0} outcomes with member ${memberId}`);
    
    return data;
  } catch (error) {
    console.error(`❌ API: Error finding outcomes with member ${memberId}:`, error);
    throw error;
  }
};

export const updateMemberInOutcome = async (outcomeId, memberData) => {
  try {
    console.log(`🔄 API: Updating member ${memberData.id} in outcome ${outcomeId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/outcomes/${outcomeId}/members/${memberData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(memberData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ API: Failed to update member in outcome:`, {
        outcomeId,
        memberId: memberData.id,
        status: response.status,
        error: errorText
      });
      throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ API: Successfully updated member ${memberData.id} in outcome ${outcomeId}`);
    
    return result;
  } catch (error) {
    console.error(`❌ API: Error updating member in outcome:`, error);
    throw error;
  }
};

// Legacy and compatibility exports
export default {
  // Generic functions
  get,
  post,
  put,
  del,
  
        // Outcomes
  getOutcomes,
  getOutcomesLightweight,
  getOutcomesLightweightByStatus,
  getOutcome,
  createOutcome,
  updateOutcome,
  deleteOutcome,
  clearApprovedBudget,
  
  // Cost Estimation
  getCostEstimation,
  saveCostEstimation,
  
  // Financial Model
  getFinancialModel,
  updateFinancialModel,
  
    // Staffing
  getStaffingData,
  clearStaffingCache,
  getRosterStaffingData,
  saveStaffingData,
  
  // IT Roster
  getITRoster,
  addITRosterMember,
  updateITRosterMember,
  deleteITRosterMember,
  
  // Rates
  getRates,
  createRate,
  updateRate,
  deleteRate,
  getRateWithColaAdjustment,
  batchRefreshOutcomeRates,
  refreshOutcomeRates,
  persistOutcomeRoleUpdates,
  
    // Unassigned Roles
  getUnassignedRoles,
  getRosterUnassignedRoles,
  assignRole,
  assignRoleToMember,
  
    // Applications
  getApplications,
  createApplication,
  updateApplication,
  deleteApplication,
  
    // Tags
  getTags,
  createTag,
  updateTag,
  deleteTag,
  assignTagToMember,
  removeTagFromMember,
  assignTagToOutcome,
  removeTagFromOutcome,
  getMemberTags,
  getOutcomeTags,
  
    // Owners
  getOwners,
  createOwner,
  addOwner,
  updateOwner,
  deleteOwner,
  syncFunctionalOwners,
  
  // Plans
  getPlans,
  getPlan,
  createPlan,
  updatePlan,
  deletePlan,
  copyPlan,
  
  // COLA Settings
  getColaSettings,
  getCOLASettings,
  updateColaSettings,
  updateCOLASettings,
  
  // Spend Data
  getSpendData,
  getSpendDataBulk,
  getUploadedOutcomeIds,
  clearSpendCache,
  getOutcomeSpend,
  updateSpendData,
  saveOutcomeSpend,
  
        // Member Allocations
  getMemberAllocations,
  getOutcomeAllocations,
  getRosterOutcomeAllocations,
  updateMemberRates,
  
  // Testing
  testPersistence,
  
    // Health
  healthCheck,
  
    // S3 Status
  checkS3Status,
  getStorageInfo,
  
    // Debug
  debugSpendData,
  
    // Data Management
  clearUploadedData,
  
  // Member Propagation
  findOutcomesWithMember,
  updateMemberInOutcome
};